import { useEffect, useState } from 'react';
import { promotionsApi } from '../../services/promotionsApi';
import { PromoCustomer } from '../../types/promotions';

interface CustomerPickerProps {
  token: string;
  selected: PromoCustomer[];
  /** min(50, slots left). */
  max: number;
  disabled?: boolean;
  onChange: (next: PromoCustomer[]) => void;
}

/** Flow C step 1a — debounced customer search (name / phone / email) with multi-select chips. */
export default function CustomerPicker({ token, selected, max, disabled, onChange }: CustomerPickerProps) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<PromoCustomer[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = term.trim();
    if (!q) { setResults([]); setError(null); return; }
    let live = true;
    const t = window.setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const page = await promotionsApi.searchCustomers(q, token);
        if (live) setResults(page.content);
      } catch (err) {
        if (live) setError(err instanceof Error ? err.message : 'Customer search failed');
      } finally {
        if (live) setSearching(false);
      }
    }, 300);
    return () => { live = false; window.clearTimeout(t); };
  }, [term, token]);

  const isSelected = (c: PromoCustomer) => selected.some((s) => s.subjectId === c.subjectId);
  const full = selected.length >= max;
  const add = (c: PromoCustomer) => { if (!isSelected(c) && !full) onChange([...selected, c]); };
  const remove = (id: string) => onChange(selected.filter((s) => s.subjectId !== id));

  return (
    <div>
      {selected.length > 0 && (
        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', marginBottom: '0.5rem' }} data-testid="picked-customers">
          {selected.map((c) => (
            <span key={c.subjectId} className="promo-chip">
              {c.name || c.phone || c.email || c.subjectId}
              <button type="button" aria-label={`Remove ${c.name ?? c.subjectId}`} disabled={disabled} onClick={() => remove(c.subjectId)}>&times;</button>
            </span>
          ))}
        </div>
      )}
      <input
        type="search"
        className="admin-form-input"
        placeholder="Search customers by name, phone or email"
        aria-label="Search customers"
        value={term}
        disabled={disabled}
        onChange={(e) => setTerm(e.target.value)}
      />
      <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginTop: '0.25rem' }}>
        {selected.length} of max {max} selected{full ? ' — limit reached' : ''}
      </div>
      {error && <div className="admin-info-banner danger" role="alert">{error}</div>}
      {searching && <div style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)', marginTop: '0.5rem' }}>Searching…</div>}
      {!searching && term.trim() && results.length === 0 && !error && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)', marginTop: '0.5rem' }}>No customers found.</div>
      )}
      {results.length > 0 && (
        <ul className="promo-picker-results" aria-label="Customer results">
          {results.map((c) => (
            <li key={c.subjectId}>
              <div>
                <div style={{ fontWeight: 500 }}>{c.name || 'Unnamed customer'}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                  {[c.phone, c.email ?? 'no email'].filter(Boolean).join(' · ')}
                </div>
              </div>
              <button type="button" className="admin-btn admin-btn-outline admin-btn-sm" disabled={disabled || isSelected(c) || full} onClick={() => add(c)}>
                {isSelected(c) ? 'Added' : 'Add'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
